import type { KeyboardEvent, MouseEvent, VoidFunctionComponent } from 'react';

import Tooltip from '../../../../generic/Tooltip';
import type { SelectOption } from '../../../../../types';
import type { TeamSelectOption } from '../../../../shared/teams/hooks/useTeamsSelectOptions';

import styles from './ProjectRowDetailsItem.module.less';

type Props = {
  item: SelectOption | TeamSelectOption;
  index: number;
  itemCount: number;
  onFilterChange: ((selected: Array<SelectOption | TeamSelectOption>) => void);
  selectedItems: Array<SelectOption | TeamSelectOption>;
};

/**
 * Component for displaying a single label or team name in the ProjectRowDetails component,
 * accounting for truncation and tooltips.
 * These elements are clickable and will add the label or team to the filters as an 'OR' list.
 * Intended to be used in a loop inside the ProjectRowDetails component.
 */
export const ProjectRowDetailsItem: VoidFunctionComponent<Props> = ({
  item,
  index,
  itemCount,
  onFilterChange,
  selectedItems,
}) => {
  const onSelectOption = (e: MouseEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>) => {
    if (e.type === 'click' || (e.type === 'keydown' && (e as KeyboardEvent).key === 'Enter')) {
      e.stopPropagation();
      const newItems = [item, ...selectedItems];
      onFilterChange(newItems);
    }
  };

  return (
    <>
      <Tooltip id={`projectRowToolTip-${item.label}`} tooltip={item.label} placement="top">
        <div className={styles.rowDetailsItem}>
          {/* We're using an anchor tag instead of a button here to allow for the truncated text without global CSS overrides */
          /* eslint-disable jsx-a11y/anchor-is-valid */}
          <a
            onClick={onSelectOption}
            onKeyDown={onSelectOption}
            role="button"
            tabIndex={0} // Add tabIndex attribute to make it focusable
          >
            {item.label}
          </a>
        </div>
      </Tooltip>
      {index !== itemCount - 1 && <span className={styles.comma}>, </span>}
    </>
  );
};
